import type { ListBuilderConfiguration } from '../types';

/**
 * Vendor Item list configuration
 * Shows items associated with vendors with image, cost, and edit/delete actions
 * Converted from JSON to TypeScript for type safety
 */
export const VENDOR_ITEM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-vendor-items-all',
			cacheTTL: 600000,
			name: 'All Vendor Items',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'image',
					width: 60,
					fields: [
						{
							id: 'item_image',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.primaryImageUrl',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								imageOptions: {
									size: 'small',
									enableViewer: true,
								},
							},
							displayStyle: 'image',
						},
					],
					headers: [{ id: 'image_header', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'item-name',
					fields: [
						{
							id: 'item-name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item',
								idField: 'itemId',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.description',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Item Name' }],
					minWidth: 250,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'item.description',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'vendor-external-id',
					fields: [
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'externalId',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Vendor External ID' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'externalId',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'vendor-cost',
					fields: [
						{
							id: 'cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'cost',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'h3', text: 'List Cost' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'cost',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					// PD-14587: negotiated (human-owned) saved price.
					id: 'vendor-negotiated-cost',
					fields: [
						{
							id: 'negotiated-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'negotiatedCost',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'h-negotiated', text: 'Negotiated' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'negotiatedCost',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					// PD-14587: last price actually paid, auto-maintained from PO saves.
					id: 'vendor-last-cost',
					fields: [
						{
							id: 'last-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lastCost',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'h-last-cost', text: 'Last Paid' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'lastCost',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'last-import',
					fields: [
						{
							id: 'last-import-date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lastImportDate',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h4', text: 'Last Import' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'lastImportDate',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 80,
					sortable: false,
					align: 'center',
				},
			],
			description: 'List of items associated with vendors',
			defaultSortId: 'column-item-name-asc',
			requiredFields: [
				'vendor_item.id',
				'vendor_item.externalId',
				'vendor_item.cost',
				'vendor_item.negotiatedCost',
				'vendor_item.lastCost',
				'vendor_item.lastImportDate',
				'vendor_item.vendorId',
				'vendor_item.itemId',
				'item.id',
				'item.description',
				'item.primaryImageUrl',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['itemSearch', 'manufacturer'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
			],
		},
		{
			baseUrl: '/vendor-items/:id',
			objectLabel: 'Vendor Item',
			objectTable: 'vendor_item',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'cost', fieldLabel: 'Cost' },
				{ fieldName: 'lastImportDate', fieldLabel: 'Last Import Date' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'item-name',
			name: 'Item Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'item.description',
				},
			],
			description: 'Sort by item name',
		},
		{
			id: 'cost',
			name: 'Cost',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'cost',
				},
			],
			description: 'Sort by cost',
		},
		{
			id: 'last-import',
			name: 'Last Import',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastImportDate',
				},
			],
			description: 'Sort by last import date (newest first)',
		},
		{
			id: 'manufacturer',
			name: 'Manufacturer',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'item.manufacturer',
				},
			],
			description: 'Sort by manufacturer',
		},
	],
};
